// components/const/Daily/PersonnelTable.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  getWorkReportDetail,
  getPersonnelUsage,
  getPersonnels,
} from "../../../api/const";
import PersonnelNameInput from "./PersonnelNameInput";

function SortablePersonnelRow({
  rowId,
  item,
  idx,
  total,
  allPersonnel,
  handleChange,
  handleSelect,
  deleteRow,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: rowId });

  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="hover:bg-gray-50"
    >
      <td className="border border-black px-2 py-1 text-center">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab px-1 py-0.5 border rounded bg-gray-50 active:cursor-grabbing"
        >
          ::
        </button>
      </td>

      <td className="border border-black px-2 py-1">
        <PersonnelNameInput
          value={item.work_type}
          options={allPersonnel}
          onInputChange={(val) => handleChange(idx, "work_type", val)}
          onSelect={(opt) => handleSelect(idx, opt)}
        />
      </td>

      <td className="border border-black px-2 py-1">
        <input
          value={item.category}
          onChange={(e) => handleChange(idx, "category", e.target.value)}
          className="w-full rounded border px-2 py-1 text-sm"
        />
      </td>

      <td className="border border-black px-2 py-1 text-center">{item.prev_total}</td>

      <td className="border border-black px-2 py-1">
        <input
          type="number"
          value={item.today}
          onChange={(e) =>
            handleChange(idx, "today", Number(e.target.value) || 0)
          }
          className="w-full rounded border px-2 py-1 text-sm"
        />
      </td>

      <td className="border border-black px-2 py-1 text-center">{total}</td>

      <td className="border border-black px-2 py-1 text-center">
        <button onClick={() => deleteRow(idx)} className="doc-del">
          삭제
        </button>
      </td>
    </tr>
  );
}

export default function PersonnelTable({
  dailyId,
  project,
  date,
  onDataChange,
}) {
  // ✅ 자재/장비 테이블과 동일하게, 처음에만 기본 1행 생성
  const [data, setData] = useState([
    {
      id: null,
      work_type: "",
      category: "",
      prev_total: 0,
      today: 0,
    },
  ]);
  const [allPersonnel, setAllPersonnel] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const getRowId = (row, index) => (row.id != null ? row.id : `row-${index}`);

  // ⭐ 드래그 종료 시 로직 (누락되어 오류 발생했던 부분)
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex(
      (row, index) => getRowId(row, index) === active.id
    );
    const newIndex = data.findIndex(
      (row, index) => getRowId(row, index) === over.id
    );

    if (oldIndex === -1 || newIndex === -1) return;

    const updated = arrayMove(data, oldIndex, newIndex);
    setData(updated);
    onDataChange(updated);
  };

  // 전체 인원 목록 로드
  useEffect(() => {
    getPersonnels().then(setAllPersonnel).catch(console.error);
  }, []);

  // 기존 리포트 로드
  useEffect(() => {
    if (!dailyId || !project || !date) return;

    (async () => {
      try {
        const report = await getWorkReportDetail(dailyId);

        if (!report.personnels || report.personnels.length === 0) {
          return;
        }

        const withPrev = await Promise.all(
          report.personnels.map(async (p) => {
            const usage = await getPersonnelUsage(project, p.id, null, date);

            const sum = usage
              .filter((u) => new Date(u.date) < new Date(date))
              .reduce((acc, u) => acc + (u.qty || 0), 0);

            return {
              id: p.id,
              work_type: p.work_type,
              category: p.category,
              prev_total: sum,
              today: p.qty,
            };
          })
        );

        setData(withPrev);
        onDataChange(withPrev);
      } catch (err) {
        console.error("인원 데이터 로드 실패:", err);
      }
    })();
  }, [dailyId, project, date]);

  const updateRow = (idx, changes) => {
    setData((prev) => {
      const updated = prev.map((r, i) =>
        i === idx ? { ...r, ...changes } : r
      );
      onDataChange(updated);
      return updated;
    });
  };

  const handleSelect = async (idx, opt) => {
    updateRow(idx, {
      id: opt.id,
      work_type: opt.work_type,
      category: opt.category,
    });

    const usage = await getPersonnelUsage(project, opt.id, null, date);
    const sum = usage.reduce((acc, u) => acc + u.qty, 0);
    updateRow(idx, { prev_total: sum });
  };

  const addRow = () => {
    const newRow = {
      id: null,
      work_type: "",
      category: "",
      prev_total: 0,
      today: 0,
    };
    const updated = [...data, newRow];
    setData(updated);
    onDataChange(updated);
  };

  const deleteRow = (idx) => {
    const updated = data.filter((_, i) => i !== idx);
    setData(updated);
    onDataChange(updated);
  };

  return (
    <div className="rounded-xl bg-white">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-body-bold">인원 현황</h3>
        {/* <button onClick={addRow} className="btn-basic-sm">
          + 행 추가
        </button> */}
      </div>

      <div className="">
        <table className="min-w-full text-sm border-t border-r border-l border-black table-fixed">
          <colgroup>
            <col style={{ width: "60px" }} /> {/* 순서 */}
            <col style={{ width: "30%" }} /> {/* 장비명 */}
            <col style={{ width: "22%" }} /> {/* 규격 */}
            <col style={{ width: "12%" }} /> {/* 전일누계 */}
            <col style={{ width: "12%" }} /> {/* 금일장비 */}
            <col style={{ width: "12%" }} /> {/* 누계장비 */}
            <col style={{ width: "60px" }} /> {/* 삭제 */}
          </colgroup>

          <thead className="doc-thead">
            <tr>
              <th className="doc-th">순서</th>
              <th className="doc-th">공종</th>
              <th className="doc-th">구분</th>
              <th className="doc-th">전일누계</th>
              <th className="doc-th">금일 인원</th>
              <th className="doc-th">누계 인원</th>
              <th className="w-[60px]"></th>
            </tr>
          </thead>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={data.map((row, index) => getRowId(row, index))}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {data.map((item, idx) => {
                  const total = item.prev_total + (item.today || 0);
                  const rowId = getRowId(item, idx);

                  return (
                    <SortablePersonnelRow
                      key={rowId}
                      rowId={rowId}
                      item={item}
                      idx={idx}
                      total={total}
                      allPersonnel={allPersonnel}
                      handleChange={(i, key, value) =>
                        updateRow(i, { [key]: value })
                      }
                      handleSelect={handleSelect}
                      deleteRow={deleteRow}
                    />
                  );
                })}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>
      <div className="flex justify-end">
        <button onClick={addRow} className="btn-basic-sm">
          + 행 추가
        </button>
      </div>
    </div>
  );
}
